/**
 * Servers (installations) admin page.
 *
 * Layout:
 *   PageHeader → HeroPanel (total / online / offline / quarantined)
 *              → Fleet freshness heatmap (OS × heartbeat-age matrix)
 *              → Toolbar (status chips + stale-only)
 *              → DataTable
 *              → ServerDrawer (full metadata + license link)
 */
import { useEffect, useMemo, useState, type JSX } from 'react';
import { Link } from 'react-router-dom';
import {
  ServerCog, RefreshCw, Trash2, PowerOff, Activity, Building2,
  KeyRound, ExternalLink,
} from 'lucide-react';
import {
  AdminApiClient, 
  type InstallationRow, type EnginesWithoutInstallation,
} from '../api';
import { bandForLiveness, labelForLiveness, fmtContactAge } from '../lib/liveness';
import { userMessageFor, failureFrom, type Failure } from '../lib/api-state';
import { usePermissions } from '../lib/permissions';
import { AccessNotice } from '../components/ui/AccessNotice';
import { SecretValue } from '../components/ui/SecretValue';
import { useSession } from '../session';
import { PageHeader } from '../components/ui/PageHeader';
import { HeroPanel, HeroKpi } from '../components/ui/HeroPanel';
import { Button } from '../components/ui/Button';
import { StatusChip, type Band } from '../components/ui/StatusChip';
import { DataTable, PrimaryWithSub, type Column } from '../components/ui/DataTable';
import { Drawer } from '../components/ui/Drawer';

type StatusFilter = 'all' | 'active' | 'deactivated' | 'quarantined' | 'unknown' | 'stale';

// VF-35: the band of a server follows the license-server's liveness verdict (shared with the Engines page),
// not a private heartbeat-age threshold.
function bandForServer(status: string, r: Pick<InstallationRow, 'liveness' | 'stale'>): Band {
  switch (status) {
    case 'active':
      return r.liveness === undefined ? (r.stale ? 'warning' : 'healthy') : bandForLiveness(r.liveness);
    case 'quarantined': return 'critical';
    case 'deactivated': return 'neutral';
    case 'unknown': return 'neutral';
    default: return 'neutral';
  }
}

function statusLabel(r: InstallationRow): string {
  return r.status === 'active' && r.liveness !== undefined ? labelForLiveness(r.liveness) : (r.stale && r.status === 'active' ? 'stale' : r.status);
}

export function ServersPage(): JSX.Element {
  const { state } = useSession();
  const [rows, setRows] = useState<InstallationRow[] | null>(null);
  const [noInstall, setNoInstall] = useState<EnginesWithoutInstallation | null>(null);
  const [filter, setFilter] = useState<StatusFilter>('all');
  const [err, setErr] = useState<Failure | null>(null);
  const [version, setVersion] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);

  const perms = usePermissions();
  const canMutate = perms.canWrite;
  const canDelete = perms.canOwner;

  const api = useMemo(
    () => new AdminApiClient({ baseUrl: '', getToken: () => state.token }),
    [state.token],
  );

  useEffect(() => {
    setRefreshing(true);
    api.installations({ limit: 1000 })
      .then((r) => { setRows(r.installations); setNoInstall(r.engines_without_installation ?? null); setErr(null); })
      .catch((e) => setErr(failureFrom(e, 'the server list')))
      .finally(() => setRefreshing(false));
  }, [api, version]);

  const onRefresh = (): void => setVersion((v) => v + 1);

  const counts = useMemo(() => {
    let total = 0, online = 0, offline = 0, quarantined = 0, stale = 0;
    for (const r of rows ?? []) {
      total += 1;
      if (r.status === 'quarantined') quarantined += 1;
      else if (r.status === 'deactivated') offline += 1;
      else if (r.stale) stale += 1;             // offline or never heard from (server verdict)
      else if (r.status === 'active') online += 1;   // online or degraded: still in contact
    }
    return { total, online, offline, quarantined, stale };
  }, [rows]);

  const filtered = useMemo(() => {
    return (rows ?? []).filter((r) => {
      if (filter === 'all') return true;
      if (filter === 'stale') return r.stale && r.status !== 'deactivated';
      if (filter === 'active') return r.status === 'active' && !r.stale;
      return r.status === filter;
    });
  }, [rows, filter]);

  const onDeactivate = async (id: string): Promise<void> => {
    const reason = prompt('Reason for deactivating this installation?') ?? '';
    if (!reason.trim() && !confirm('Proceed without a reason?')) return;
    try {
      await api.deactivateInstallation(id, { reason: reason || undefined });
      onRefresh();
    } catch (e) {
      alert(userMessageFor(e));
    }
  };
  const onDelete = async (id: string): Promise<void> => {
    if (!confirm('PERMANENTLY delete this installation + all its heartbeats? Audit log keeps a snapshot.')) return;
    try {
      await api.deleteInstallation(id);
      setOpenId(null);
      onRefresh();
    } catch (e) {
      alert(userMessageFor(e));
    }
  };

  const focused = useMemo(
    () => (rows ?? []).find((r) => r.id === openId) ?? null,
    [rows, openId],
  );

  const columns: Column<InstallationRow>[] = [
    {
      key: 'company',
      header: 'Customer',
      sortable: true,
      sortValue: (r) => r.company_name,
      cell: (r) => (
        <PrimaryWithSub
          primary={r.company_name}
          sub={`${r.plan_tier}${r.license_type === 'trial' ? ' · trial' : ''}`}
        />
      ),
    },
    {
      key: 'os',
      header: 'OS',
      sortable: true,
      sortValue: (r) => r.os_distro ?? r.os_platform,
      cell: (r) => (
        <span style={{ fontSize: '0.75rem' }}>
          <span style={{ color: 'var(--text-primary)', fontWeight: 500 }}>
            {r.os_distro ?? r.os_platform}
          </span>{' '}
          <span style={{ color: 'var(--text-muted)' }}>{r.os_version ?? ''}</span>
        </span>
      ),
    },
    {
      key: 'binary',
      header: 'Binary',
      cell: (r) =>
        r.binary_version ? (
          <code
            style={{
              fontSize: '0.6875rem',
              background: 'var(--bg-overlay)',
              padding: '2px 6px',
              borderRadius: 4,
              border: '1px solid var(--border)',
              color: 'var(--text-muted)',
            }}
          >{r.binary_version}</code>
        ) : <span style={{ color: 'var(--text-muted)' }}>—</span>,
    },
    {
      key: 'heartbeat',
      header: 'Last contact',
      align: 'right',
      sortable: true,
      // Never-seen servers have no age and sort last in both directions.
      sortValue: (r) => r.contact_age_seconds ?? r.heartbeat_age_seconds,
      cell: (r) => <HeartbeatCell r={r} />,
    },
    {
      key: 'status',
      header: 'Status',
      cell: (r) => (
        <StatusChip band={bandForServer(r.status, r)}>
          {statusLabel(r)}
        </StatusChip>
      ),
    },
  ];

  const statusChips: Array<{ key: StatusFilter; label: string; count: number }> = [
    { key: 'all',          label: 'All',          count: counts.total },
    { key: 'active',       label: 'Online',       count: counts.online },
    { key: 'stale',        label: 'Offline',      count: counts.stale },
    { key: 'deactivated',  label: 'Deactivated',  count: counts.offline },
    { key: 'quarantined',  label: 'Quarantined',  count: counts.quarantined },
  ];

  return (
    <div className="page">
      <PageHeader
        title="Servers"
        subtitle="Every installed Vloud engine. Online / offline is the same rule as the Engines page: the freshest of the licence pull, registration and heartbeat. Deactivating frees an activation slot."
        icon={ServerCog}
        accent="#34d399"
        actions={
          <Button variant="outline" size="sm" onClick={onRefresh}>
            <RefreshCw width={14} height={14}
              style={{ animation: refreshing ? 'spin 900ms linear infinite' : undefined }} />
            Refresh
          </Button>
        }
      />

      <AccessNotice reason={canMutate ? null : `${perms.writeReason ?? ''} Deactivating and deleting installations is not available to you.`} />

      {rows && (<>
      <HeroPanel
        icon={Activity}
        accent="#34d399"
        title="Fleet liveness"
        subtitle="How many engines are checking in vs sitting dark."
        kpis={[
          <HeroKpi key="t" label="Total" value={counts.total} hint="installations" />,
          <HeroKpi key="o" label="Online" value={counts.online} band={counts.online > 0 ? 'healthy' : 'neutral'} />,
          <HeroKpi key="s" label="Offline" value={counts.stale}
                   band={counts.stale > 0 ? 'warning' : 'neutral'} hint="no recent contact" />,
          <HeroKpi key="n" label="No installation" value={noInstall?.total ?? 0}
                   hint={noInstall ? `${noInstall.online + noInstall.degraded} live · Free / unlicensed engines (see Engines)` : 'Free / unlicensed engines'} />,
          <HeroKpi key="q" label="Quarantined" value={counts.quarantined}
                   band={counts.quarantined > 0 ? 'critical' : 'neutral'} />,
        ]}
      />

      <div style={{ marginTop: 16 }}>
        <FleetHeatmap rows={rows} />
      </div>
      </>)}

      <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
        {rows && <ChipRow chips={statusChips} value={filter} onChange={setFilter} />}

        <DataTable
          rows={filtered}
          columns={columns}
          rowKey={(r) => r.id}
          onRowClick={(r) => setOpenId(r.id)}
          loading={!rows && !err}
          error={err}
          onRetry={onRefresh}
          emptyTitle="No servers match"
          emptyDescription="Adjust filters or refresh to see more."
        />
      </div>

      <ServerDrawer
        row={focused}
        canMutate={canMutate}
        canDelete={canDelete}
        writeReason={perms.writeReason}
        onClose={() => setOpenId(null)}
        onDeactivate={focused ? () => void onDeactivate(focused.id) : undefined}
        onDelete={focused ? () => void onDelete(focused.id) : undefined}
      />
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────
 * Heatmap: rows = OS platform, columns = heartbeat-age bucket.
 * Cells colored by count using a single-hue (accent) ramp.
 * Without CPU/RAM/disk data, freshness × OS is the most actionable
 * 2D view we can ship today.
 * ────────────────────────────────────────────────────────────────── */

function FleetHeatmap({ rows }: { rows: InstallationRow[] }): JSX.Element {
  const buckets = ['<5m', '<1h', '<24h', 'stale', 'never'] as const;

  const matrix = useMemo(() => {
    const oses = new Map<string, number[]>();
    const bumpInto = (os: string, idx: number): void => {
      const arr = oses.get(os) ?? buckets.map(() => 0);
      arr[idx] = (arr[idx] ?? 0) + 1;
      oses.set(os, arr);
    };
    for (const r of rows) {
      const os = (r.os_distro ?? r.os_platform ?? 'unknown').toLowerCase();
      const age = r.contact_age_seconds !== undefined ? r.contact_age_seconds : r.heartbeat_age_seconds;
      let idx = 4; // never
      if (age != null) {
        if (age < 300) idx = 0;
        else if (age < 3600) idx = 1;
        else if (age < 86400) idx = 2;
        else idx = 3; // stale
      }
      bumpInto(os, idx);
    }
    return Array.from(oses.entries()).sort((a, b) => {
      const sumA = a[1].reduce((s, v) => s + v, 0);
      const sumB = b[1].reduce((s, v) => s + v, 0);
      return sumB - sumA;
    });
  }, [rows]);

  if (matrix.length === 0) {
    return (
      <section
        style={{
          padding: 16,
          borderRadius: 14,
          border: '1px solid var(--border)',
          background: 'var(--bg-raised)',
          color: 'var(--text-muted)',
          fontSize: '0.8125rem',
        }}
      >
        Fleet heatmap will populate once there are heartbeats to chart.
      </section>
    );
  }

  const max = Math.max(1, ...matrix.flatMap(([, vs]) => vs));

  return (
    <section
      style={{
        padding: 16,
        borderRadius: 14,
        border: '1px solid var(--border)',
        background: 'var(--bg-raised)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <Activity width={14} height={14} color="var(--text-muted)" />
        <span style={{
          fontSize: '0.6875rem',
          textTransform: 'uppercase',
          letterSpacing: '0.08em',
          color: 'var(--text-muted)',
          fontWeight: 600,
        }}>
          Fleet freshness · OS × heartbeat age
        </span>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: `120px repeat(${buckets.length}, minmax(60px, 1fr))`,
          gap: 4,
          alignItems: 'center',
        }}
      >
        <span />
        {buckets.map((b) => (
          <span
            key={b}
            style={{
              fontSize: '0.6875rem', color: 'var(--text-muted)',
              textAlign: 'center', fontVariantNumeric: 'tabular-nums',
            }}
          >
            {b}
          </span>
        ))}
        {matrix.map(([os, counts]) => (
          <HeatRow key={os} os={os} counts={counts} max={max} />
        ))}
      </div>
    </section>
  );
}

function HeatRow({
  os, counts, max,
}: { os: string; counts: number[]; max: number }): JSX.Element {
  return (
    <>
      <span
        style={{
          fontSize: '0.75rem',
          color: 'var(--text-muted)',
          textTransform: 'capitalize',
          paddingRight: 8,
          textAlign: 'right',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {os}
      </span>
      {counts.map((v, i) => {
        const ratio = v === 0 ? 0 : 0.15 + 0.85 * (v / max);
        // Cooler tones (live) → warmer (stale) — gives the row a heat gradient.
        const baseHue = i === 0 ? 152
                      : i === 1 ? 168
                      : i === 2 ? 200
                      : i === 3 ? 30
                                : 0;
        const bg = v === 0
          ? 'rgba(148,163,184,0.06)'
          : `hsla(${baseHue} 80% 50% / ${ratio})`;
        return (
          <div
            key={i}
            title={`${v} ${v === 1 ? 'server' : 'servers'}`}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: 30,
              borderRadius: 6,
              background: bg,
              border: '1px solid rgba(148,163,184,0.10)',
              color: ratio > 0.45 ? '#0b0f19' : 'var(--text-muted)',
              fontWeight: 500,
              fontVariantNumeric: 'tabular-nums',
              fontSize: '0.75rem',
            }}
          >
            {v > 0 ? v : ''}
          </div>
        );
      })}
    </>
  );
}

/* ──────────────────────────────────────────────────────────────────
 * Heartbeat cell with relative time + stale tone
 * ────────────────────────────────────────────────────────────────── */

function HeartbeatCell({ r }: { r: InstallationRow }): JSX.Element {
  const at = r.last_contact_at !== undefined ? r.last_contact_at : r.last_heartbeat_at;
  const age = r.contact_age_seconds !== undefined ? r.contact_age_seconds : r.heartbeat_age_seconds;
  if (!at || age == null) {
    return <span style={{ color: 'var(--danger)', fontSize: '0.75rem' }}>never</span>;
  }
  const tone = r.stale ? 'var(--danger)' : 'var(--text-primary)';
  return (
    <span
      style={{
        display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-end',
      }}
    >
      <span style={{ fontSize: '0.75rem', color: tone, fontWeight: 500 }}>
        {fmtContactAge(age)}
      </span>
      <span style={{ fontSize: '0.6875rem', color: 'var(--text-muted)' }}>
        {at.slice(11, 16)} UTC
      </span>
    </span>
  );
}

function fmtAgo(seconds: number): string {
  if (seconds < 60) return `${seconds}s ago`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
}

/* ──────────────────────────────────────────────────────────────────
 * Chip row
 * ────────────────────────────────────────────────────────────────── */

function ChipRow<T extends string>({
  chips, value, onChange,
}: {
  chips: Array<{ key: T; label: string; count: number }>;
  value: T;
  onChange: (next: T) => void;
}): JSX.Element {
  return (
    <div
      role="group"
      aria-label="Filters"
      style={{
        display: 'inline-flex', gap: 4, padding: 4,
        borderRadius: 12, border: '1px solid var(--border)', background: 'var(--bg-overlay)',
        flexWrap: 'wrap', width: 'fit-content',
      }}
    >
      {chips.map((c) => {
        const active = c.key === value;
        return (
          <button
            key={c.key}
            aria-pressed={active}
            onClick={() => onChange(c.key)}
            style={{
              display: 'inline-flex', gap: 5, alignItems: 'center',
              padding: '5px 9px', border: 'none', borderRadius: 8,
              background: active ? 'var(--bg-raised)' : 'transparent',
              color: active ? 'var(--text-primary)' : 'var(--text-muted)',
              fontSize: '0.75rem', fontWeight: 500, cursor: 'pointer',
            }}
          >
            {c.label}
            <span
              style={{
                fontVariantNumeric: 'tabular-nums',
                fontSize: '0.6875rem',
                color: 'var(--text-muted)',
                background: 'var(--bg-overlay)',
                border: '1px solid var(--border)',
                padding: '0 6px',
                borderRadius: 999,
                minWidth: 18, textAlign: 'center',
              }}
            >{c.count}</span>
          </button>
        );
      })}
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────
 * Server drawer
 * ────────────────────────────────────────────────────────────────── */

function ServerDrawer({
  row, canMutate, canDelete, writeReason, onClose, onDeactivate, onDelete,
}: {
  row: InstallationRow | null;
  canMutate: boolean;
  canDelete: boolean;
  writeReason: string | null;
  onClose: () => void;
  onDeactivate?: () => void;
  onDelete?: () => void;
}): JSX.Element {
  return (
    <Drawer
      open={!!row}
      onClose={onClose}
      width="md"
      title={
        row ? (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <ServerCog width={14} height={14} />
            <span>{row.company_name}</span>
          </span>
        ) : ''
      }
      subtitle={
        row ? `${row.os_distro ?? row.os_platform} · ${row.binary_version ?? '—'}` : undefined
      }
      footer={
        row && !canMutate && writeReason ? (
          <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{writeReason}</span>
        ) : row && canMutate && (
          <>
            {canDelete && onDelete && (
              <Button variant="ghost" size="sm" onClick={onDelete}>
                <Trash2 width={14} height={14} />
                Delete
              </Button>
            )}
            {row.status !== 'deactivated' && onDeactivate && (
              <Button variant="destructive" size="sm" onClick={onDeactivate}>
                <PowerOff width={14} height={14} />
                Deactivate
              </Button>
            )}
          </>
        )
      }
    >
      {row && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <Section title="Status">
            <div style={{ display: 'grid', gap: 8, gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
              <KV label="Status">
                <StatusChip band={bandForServer(row.status, row)}>
                  {statusLabel(row)}
                </StatusChip>
              </KV>
              <KV label="Last contact">
                {row.contact_age_seconds != null ? fmtContactAge(row.contact_age_seconds) : 'never'}
              </KV>
              <KV label="Last heartbeat">
                {row.heartbeat_age_seconds != null ? fmtAgo(row.heartbeat_age_seconds) : 'never'}
              </KV>
              <KV label="Activated">{row.activated_at.slice(0, 16).replace('T', ' ')}Z</KV>
              <KV label="Activation key">
                <SecretValue value={row.activation_key} redacted={row.activation_key_redacted} />
              </KV>
            </div>
          </Section>

          <Section title="System">
            <div style={{ display: 'grid', gap: 8, gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
              <KV label="Platform">{row.os_platform}</KV>
              <KV label="Distro">{row.os_distro ?? '—'}</KV>
              <KV label="OS version">{row.os_version ?? '—'}</KV>
              <KV label="Binary">{row.binary_version ?? '—'}</KV>
            </div>
          </Section>

          <Section title="Links" icon={ExternalLink}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <LinkRow
                icon={KeyRound}
                label="License"
                value={row.license_id}
                href={`/licenses?focus=${encodeURIComponent(row.license_id)}`}
              />
              <LinkRow
                icon={Building2}
                label="Customer"
                value={row.customer_id}
                href={`/customers?focus=${encodeURIComponent(row.customer_id)}`}
              />
            </div>
          </Section>

          <Section title="Identifiers">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: '0.75rem', color: 'var(--text-muted)' }}>
              <div>installation: <code>{row.id}</code></div>
              <div>plan: <code>{row.plan_tier}</code></div>
              <div>license_type: <code>{row.license_type}</code></div>
            </div>
          </Section>
        </div>
      )}
    </Drawer>
  );
}

function LinkRow({
  icon: Icon, label, value, href,
}: {
  icon: typeof KeyRound;
  label: string;
  value: string;
  href: string;
}): JSX.Element {
  return (
    <Link
      to={href}
      style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '8px 10px',
        borderRadius: 8,
        background: 'var(--bg-overlay)',
        border: '1px solid var(--border)',
        textDecoration: 'none',
        color: 'var(--text-primary)',
      }}
    >
      <Icon width={12} height={12} color="var(--text-muted)" />
      <span style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
        {label}
      </span>
      <code style={{ marginLeft: 'auto', fontSize: '0.75rem' }}>{value.slice(0, 16)}…</code>
      <ExternalLink width={12} height={12} color="var(--text-muted)" />
    </Link>
  );
}

/* Helpers */

function Section({
  title, icon: Icon, children,
}: { title: string; icon?: typeof KeyRound; children: React.ReactNode }): JSX.Element {
  return (
    <section>
      <h3
        style={{
          margin: 0, marginBottom: 8,
          fontSize: '0.6875rem', fontWeight: 600,
          textTransform: 'uppercase', letterSpacing: '0.08em',
          color: 'var(--text-muted)',
          display: 'flex', alignItems: 'center', gap: 6,
        }}
      >
        {Icon && <Icon width={11} height={11} />}
        {title}
      </h3>
      {children}
    </section>
  );
}

function KV({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <span style={{ fontSize: '0.6875rem', color: 'var(--text-muted)' }}>{label}</span>
      <div style={{ fontSize: '0.8125rem' }}>{children}</div>
    </div>
  );
}

